/** @format */

import moment from 'moment';

import { useLoader } from '@/ducks/bookingNew/hooks/useLoader';
import { dateStringToJsonUtc } from '@/utils/helpers/datoToJsonUtc';
import { apiRoomAvailability } from '../../../api/apiRoomAvailability';
import { apiRoomAvailabilityReduced } from '../../../api/apiRoomAvailabilityReduced';
import { useAppDispatch } from '../../../redux/hooks';
import { RoomAvailability } from '../types';

export const useRoomAvailability = () => {
  const dispatch = useAppDispatch();
  const { removeLoader } = useLoader('availability');

  const getRoomAvailability = async ({
    code,
    checkInDate,
    commercialConditionsId,
    reduced,
    controller,
    portalCode,
  }: {
    code: string;
    checkInDate: string;
    commercialConditionsId?: string;
    reduced?: boolean;
    controller?: AbortController;
    portalCode: string;
  }): Promise<RoomAvailability | undefined> => {
    const payload = {
      code,
      checkInDate: moment(checkInDate).format('YYYY-MM-DD'),
      commercialConditionsId,
      controller,
      portalCode,
    };

    dispatch({ type: 'BOOKING_AVAILABILITY_REQUEST' });

    try {
      const availability = reduced ? await apiRoomAvailabilityReduced(payload) : await apiRoomAvailability(payload);

      if (availability) {
        const checkInDates = availability.checkInDates.map(date => dateStringToJsonUtc(date));
        const validCheckoutDates = reduced
          ? []
          : availability.validCheckoutDates.map(date => dateStringToJsonUtc(date));

        dispatch({
          type: 'BOOKING_AVAILABILITY_SUCCESS',
          payload: {
            price: availability.price,
            discountedPrice: availability.discountedPrice,
            discountUntil: dateStringToJsonUtc(availability.discountUntil || ''),
            visibleOnline: availability.visibleOnline,
            availableFrom: dateStringToJsonUtc(availability.availableFrom),
            availableTo: dateStringToJsonUtc(availability.availableTo),
            bedAvailabilityId: availability.bedAvailabilityId,
            bedCode: availability.bedCode,
            negotiating: availability.negotiating,
            status: availability.status,
            checkoutDateFrom: dateStringToJsonUtc(availability.checkoutDateFrom),
            checkoutDateTo: dateStringToJsonUtc(availability.checkoutDateTo),
            salableOnline: availability.salableOnline,
            defaultCheckoutDate: dateStringToJsonUtc(availability.defaultCheckoutDate),
            evaluedExitWindows: availability.evaluedExitWindows,
            checkInDates,
            validCheckoutDates,
          },
        });
        if (availability && availability.salableOnline === false) {
          dispatch({ type: 'BOOKING_AVAILABILITY_FAILURE' });
        }
        return availability;
      } else {
        dispatch({ type: 'BOOKING_AVAILABILITY_FAILURE' });
      }
    } catch (error) {
      dispatch({ type: 'BOOKING_AVAILABILITY_FAILURE' });
    } finally {
      removeLoader();
    }
  };

  return { getRoomAvailability };
};
